import { useEffect, useContext, useRef, useState } from "react";
import { UserContext } from "../../Context";
import { motion } from "framer-motion";
import { ClipboardList, Trash2 } from "lucide-react";
import {
  GenericTable,
  Spinner,
  Pagination,
  FilterDropdown,
  SearchableDropdown,
  RoleValidator,
} from "../../Components/Common";
import { formatDateDDMMYYYY, toProperCase } from "../../Utils";
import { useNavigate } from "react-router-dom";
import { CREATE_SAN, EDIT_SAN, VIEW_DETAILED_SAN } from "../../routes";
import {
  getSANStatusColor,
  MATERIAL_COLUMNS,
  MATERIAL_LABELS,
  SAN_STATUS,
  SAN_STATUS_VALUE_LABELS,
  ROLES,
} from "./constants.js";
import { MdAdd, MdModeEditOutline } from "react-icons/md";
import { FiInfo } from "react-icons/fi";
import { useSAN } from "./Hooks/useSAN.js";
import { useWarehouse } from "../Inventory/Hooks/useWarehouse.js";


const StockAdjustmentNoteList = () => {
  const {
    loading,
    filters,
    setFilters,
    page,
    totalPages,
    rowsPerPage,
    onPageChange,
    onPageSizeChange,
    fetchAllSANs,
    deleteSAN,
  } = useSAN();
  const { fetchWarehouses } = useWarehouse();
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const dropdownRefs = {
    status: useRef(null),
  };
  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);

  const loadSANs = async () => {
    const data = await fetchAllSANs();
    if (data) setMaterials(data);
    else setMaterials([]);
  };

  useEffect(() => {
    loadSANs();
  }, [page, rowsPerPage, filters]);

  const handleDeleteMaterial = async (SANItem) => {
    const data = await deleteSAN(SANItem.id);
    if (data) {
      setMaterials((prev) => prev.filter((item) => item.id !== SANItem.id));
    }
  };

  const { user } = useContext(UserContext);
  const currentUserRole = user?.role;

  const canEditSAN = (role, status, isSiteWarehouse) => {
    if (!role) return false;
    if (isSiteWarehouse) {
      if (status === SAN_STATUS.DRAFT) {
        return role === ROLES.SITE_ENGINEER;
      }
      if (status === SAN_STATUS.PENDING_FOR_APPROVAL) {
        return role === ROLES.PROJECT_MANAGER;
      }
      return false;
    }
    else {
      if (status === SAN_STATUS.DRAFT) {
        return role === ROLES.INVENTORY_INCHARGE;
      }
      if (status === SAN_STATUS.PENDING_FOR_APPROVAL) {
        return role === ROLES.INVENTORY_ADMIN;
      }
      return false;
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefs.status.current &&
        !dropdownRefs.status.current.contains(event.target)
      ) {
        setIsStatusFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Render Field Values for Table
  const renderFields = (key, rowData) => {
    switch (key) {
      case "created": {
        const date = rowData.created.split("T")[0];
        return formatDateDDMMYYYY(date);
      }
      case "status":
        return (
          <span
            className={`px-1.5 py-1 rounded-md text-xs font-medium ${getSANStatusColor(
              rowData.status
            )}`}
          >
            {toProperCase(rowData.status)}
          </span>
        );
      case "actions":
        return (
          <div className="flex items-center gap-4">
            <FiInfo
              title="View Details"
              className="cursor-pointer text-primary hover:text-secondary text-lg"
              onClick={() =>
                navigate(VIEW_DETAILED_SAN.replace(":instanceId", rowData.id))
              }
            />
            {/* Edit Button */}
            <RoleValidator
              allowedRoles={[
                ROLES.SITE_ENGINEER,
                ROLES.INVENTORY_INCHARGE,
                ROLES.PROJECT_MANAGER,
                ROLES.INVENTORY_ADMIN,
              ]}
            >
              <button
                onClick={() =>
                  navigate(EDIT_SAN.replace(":instanceId", rowData.id))
                }
                disabled={!canEditSAN(
                  currentUserRole,
                  rowData.status,
                  rowData.receiving_warehouse_is_site
                )}
                className={`${canEditSAN(
                  currentUserRole,
                  rowData.status,
                  rowData.receiving_warehouse_is_site
                )
                    ? "text-blue-500 hover:text-blue-700"
                    : "text-gray-400 cursor-not-allowed"
                  }`}
                title={
                  canEditSAN(
                    currentUserRole,
                    rowData.status,
                    rowData.receiving_warehouse_is_site
                  )
                    ? "Edit"
                    : `Cannot Edit in ${toProperCase(rowData.status)} state or insufficient permissions`
                }
              >
                <MdModeEditOutline className="h-4 w-4" />
              </button>

            </RoleValidator>

            {/* Delete Button */}
            <RoleValidator
              allowedRoles={[
                ROLES.SITE_ENGINEER,
                ROLES.INVENTORY_INCHARGE,
                ROLES.PROJECT_MANAGER,
                ROLES.INVENTORY_ADMIN
              ]}
            >
            <button
              onClick={() => {
                if (
                  window.confirm(
                    `Are you sure you want to delete this SAN ? This action cannot be undone.`
                  )
                ) {
                  handleDeleteMaterial(rowData);
                }
              }}
              disabled={rowData.status !== SAN_STATUS.DRAFT}
              className={`${
                rowData.status === SAN_STATUS.DRAFT
                  ? "text-red-500 hover:text-red-700"
                  : "text-gray-400 cursor-not-allowed"
              }`}
              title={
                rowData.status === SAN_STATUS.DRAFT
                  ? "Remove"
                  : `Cannot delete in ${toProperCase(
                      rowData.status
                    )} state or insufficient permissions`
              }
            >
              <Trash2 size={18} />
            </button>
            </RoleValidator>
          </div>
        );
      default:
        return rowData[key];
    }
  };

  // Mobile Card View
  const renderMobileCards = () => (
    <ul className="space-y-3">
      {materials.length > 0 ? (
        materials.map((item) => {
          return (
            <li
              key={item.id}
              className="p-2 rounded-lg border text-sm flex justify-between shadow-sm"
              onClick={() =>
                navigate(VIEW_DETAILED_SAN.replace(":instanceId", item.id))
              }
            >
              <div className="max-w-[60%]">
                <div className="font-semibold text-gray-800 mb-1 truncate">
                  {item.receiving_warehouse_name}
                </div>
                <div className="text-xs text-gray-600">
                  <span className="font-medium">Raised On</span>{" "}
                  {formatDateDDMMYYYY(item.created.split("T")[0])}{" "}
                  <span className="font-medium">By</span> {item.created_by}
                </div>
              </div>
              <div className="flex flex-col items-end justify-between gap-2">
                <div className="flex items-center gap-4">
                  <RoleValidator
              allowedRoles={[
                ROLES.SITE_ENGINEER,
                ROLES.INVENTORY_INCHARGE,
                ROLES.PROJECT_MANAGER,
                ROLES.INVENTORY_ADMIN
              ]}
            >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(EDIT_SAN.replace(":instanceId", item.id));
                    }}
                    disabled={item.status !== SAN_STATUS.DRAFT}
                    className={`${
                      item.status === SAN_STATUS.DRAFT
                        ? "text-blue-500 hover:text-blue-700"
                        : "text-gray-400 cursor-not-allowed"
                    }`}
                    title={
                      item.status === SAN_STATUS.DRAFT
                        ? "Edit"
                        : `Cannot Edit in ${toProperCase(
                            item.status
                          )} state or insufficient permissions`
                    }
                  >
                    <MdModeEditOutline className="h-4 w-4" />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        window.confirm(
                          `Are you sure you want to delete this SAN? This action cannot be undone.`
                        )
                      ) {
                        handleDeleteMaterial(item);
                      }
                    }}
                    disabled={item.status !== SAN_STATUS.DRAFT}
                    className={`${
                      item.status === SAN_STATUS.DRAFT
                        ? "text-red-500 hover:text-red-700"
                        : "text-gray-400 cursor-not-allowed"
                    }`}
                    title={
                      item.status === SAN_STATUS.DRAFT
                        ? "Remove"
                        : `Cannot delete in ${toProperCase(
                            item.status
                          )} state or insufficient permissions`
                    }
                  >
                    <Trash2 size={14} />
                    </button>
                  </RoleValidator>
                </div>

                <span
                  className={`px-1.5 py-1 rounded-md text-xs font-medium ${getSANStatusColor(
                    item.status
                  )}`}
                >
                  {toProperCase(item.status)}
                </span>
              </div>
            </li>
          );
        })
      ) : (
        <div className="w-full h-[280px] flex justify-center items-center text-gray-300 flex-col">
          <ClipboardList size={35} />
          <span>No Material Requests</span>
        </div>
      )}
    </ul>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="px-4 py-4"
    >
      <div className="flex justify-between items-center mb-4 mt-2 md:mt-0">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          Stock Adjustment Notes
        </h2>

        {/* Desktop button */}
        <div className="hidden md:block">
          <RoleValidator
            allowedRoles={[
              ROLES.SITE_ENGINEER,
              ROLES.INVENTORY_INCHARGE
            ]}
          >
          <button
            onClick={() => navigate(CREATE_SAN)}
            className="bg-primary text-white text-sm px-3 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            + Create SAN
          </button>
          </RoleValidator>
        </div>

        {/* Mobile button */}
        <div className="block md:hidden">
          <RoleValidator
            allowedRoles={[
              ROLES.SITE_ENGINEER,
              ROLES.INVENTORY_INCHARGE
            ]}
          >
          <button
            onClick={() => navigate(CREATE_SAN)}
            className="bg-primary text-white px-2 py-1 mt-1 rounded-md hover:bg-[#bde0fe] max-h-fit"
            aria-label="Add SAN"
          >
            <MdAdd size={28} />
          </button>
          </RoleValidator>
        </div>
      </div>

      <div className="bg-white w-full rounded-2xl shadow-md border px-4 py-3 mb-2">
        <div className="flex flex-wrap gap-2 items-center">
          <SearchableDropdown
            fetchFunction={fetchWarehouses}
            onSelect={(warehouse) =>
              setFilters({ ...filters, receiving_warehouse: warehouse })
            }
            valueKey="id"
            labelKey="name"
            placeholder={"Select Warehouse"}
            width="64"
            includeAllOption={true}
          />
          <div
            ref={dropdownRefs.status}
            className="flex justify-center items-center"
          >
            <FilterDropdown
              options={[
                { value: "", label: "All" },
                ...SAN_STATUS_VALUE_LABELS,
              ]}
              value={filters.status || ""}
              onChange={(status) => {
                setFilters({ ...filters, status })
                setIsStatusFilterOpen(false);
              }}
              isOpen={isStatusFilterOpen}
              onToggle={() => setIsStatusFilterOpen(!isStatusFilterOpen)}
              label={"Status"}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-[60vh]">
          <Spinner />
        </div>
      ) : (
        <div>
          {/* Desktop View */}
          <div className="hidden md:block h-[54vh] overflow-y-auto">
            <GenericTable
              tableColumns={MATERIAL_COLUMNS}
              tableRows={materials}
              columnLabels={MATERIAL_LABELS}
              renderField={renderFields}
            />
          </div>

          {/* Mobile View */}
          <div className="block md:hidden h-[62vh] overflow-y-auto">
            {renderMobileCards()}
          </div>

          {/* Pagination */}
          <Pagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </motion.div>
  );
};

export default StockAdjustmentNoteList;
